import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { onboardingQuestionnaireActions } from '../../../../../../../store';
import { getAllFieldsTouched } from '../../../../../../common';
import {
  areSavedValuesEqual,
  getPatchData,
  getSavedFieldValue,
  getSavedFieldValues,
  getValuesWithFieldOverride,
  isReferralDateFieldValid,
  isReferralFieldSchemaValid,
} from './utils';

import type { FormikProps } from 'formik';
import type { AppDispatch } from '../../../../../../../store';
import type { ReferralSavedFieldValues } from './types';
import type { ReferralFieldName, ReferralFieldValue } from './types';
import type { ReferralFormValues } from '../../../../types';

export default function useSaveReferralFieldOnBlur(
  formik: FormikProps<ReferralFormValues>,
  initialValues: ReferralFormValues,
) {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { onboardingQuestionnaireId } = useParams<'onboardingQuestionnaireId'>();
  const savedFieldValuesRef = useRef<ReferralSavedFieldValues>(getSavedFieldValues(initialValues));
  const savingPromiseRef = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    savedFieldValuesRef.current = getSavedFieldValues(initialValues);
  }, [initialValues]);

  return useCallback(
    async (name: ReferralFieldName, value?: ReferralFieldValue) => {
      if (!onboardingQuestionnaireId) {
        return;
      }

      const values = getValuesWithFieldOverride(formik.values, name, value);

      if (!isReferralDateFieldValid(name, values)) {
        return;
      }

      if (!(await isReferralFieldSchemaValid(name, values))) {
        return;
      }

      const savedFieldValue = getSavedFieldValue(name, values);

      if (areSavedValuesEqual(savedFieldValuesRef.current[name], savedFieldValue)) {
        return;
      }

      const previousSave = savingPromiseRef.current;
      const currentSave = (async () => {
        try {
          await previousSave;
        } catch {
          // Swallow prior save errors so this save still proceeds.
        }

        const resultAction = await dispatch(
          onboardingQuestionnaireActions.updateOnboardingQuestionnaireReferralInfo({
            onboardingQuestionnaireId,
            data: getPatchData(name, values),
            getAccessTokenSilently,
          }),
        );

        if (
          !onboardingQuestionnaireActions.updateOnboardingQuestionnaireReferralInfo.fulfilled.match(
            resultAction,
          )
        ) {
          return;
        }

        const errors = await formik.validateForm(values);

        formik.setErrors(errors);
        formik.setTouched(getAllFieldsTouched(values), false);
        savedFieldValuesRef.current = {
          ...savedFieldValuesRef.current,
          [name]: savedFieldValue,
        };
      })();

      savingPromiseRef.current = currentSave;
      await currentSave;
    },
    [dispatch, formik, getAccessTokenSilently, onboardingQuestionnaireId],
  );
}
